import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import gql from 'graphql-tag';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ProcessTaskStatusType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getBadgeStatus, ReviewBadgeStatus} from 'notebook/reviews/ReviewBadge';
import {getStatusIcon} from 'notebook/reviews/ReviewUtil';
import {
  DocumentStatus,
  WorksheetStepDocumentSelection,
  WorksheetViewActionType,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {
  PMEIProcedureQuery,
  PMEIProcedureQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_procedureRun_ProcedureRunNode as ProcedureRunNode,
  PMEIProcedureQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_procedureRun_ProcedureRunNode_methodExecutionInstances_ProcedureMethodExecutionInstanceNode_procedureRunConditionReplicate_ProcedureRunConditionReplicateNode as ProcedureRunConditionReplicateNode,
  PMEIProcedureQueryVariables,
} from 'procedures/worksheets/__generated/PMEIProcedureQuery';
import DisabledMethodAndWorksheetDropdowns from 'procedures/worksheets/DisabledMethodAndWorksheetDropdowns';
import TaskStatusSummaryHeader from 'procedures/worksheets/TaskStatusSummaryHeader';
import WorksheetStudyLink from 'procedures/worksheets/WorksheetStudyLink';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

const INACCESSIBLE_WORKSHEET_NAME = '(No Access)';

export const PMEI_PROCEDURE_QUERY = gql`
  query PMEIProcedureQuery($worksheetId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    procedureMethodExecutionInstance(worksheetId: $worksheetId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        procedureRun {
          id
          procedureType
          study {
            ... on StudyNode {
              id
              name
              editUrl
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          methodExecutionInstances {
            methodTask {
              ... on ProcessTaskNode {
                id
                status {
                  id
                  statusType
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            id
            procedureRunConditionReplicate {
              id
              # this might be something we need to reconsider when we rework how we subscribe to realtime updates
              processRootTask {
                ... on ProcessTaskNode {
                  id
                  process {
                    id
                  }
                }
                ... on InaccessibleInfoNode {
                  ...InaccessibleInfoGraphQL
                }
              }
            }
            planMethodDefinitionVersion {
              ... on BaseProcedureMethodDefinitionVersionInterface {
                id
                name
                sourceMethodId
              }
            }
            templateMethodVersion {
              ... on ProcedureMethodDefinitionVersionNode {
                id
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            worksheet {
              ... on WorksheetNode {
                id
                name
              }
              ... on ReviewableInterface {
                id
                review {
                  id
                  status
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
export const LOADING_METHOD_NAME = 'loading...';

export interface SelectWorksheetDropdownProps {
  worksheetId: string;
  methodId: string;
  procedureRun: ProcedureRunNode;
  onSetWorksheet: (id: string) => boolean;
  onSetSelectedStep: (selectedStep: WorksheetStepDocumentSelection) => void;
  selectedStep: WorksheetStepDocumentSelection | null;
  refetch: () => Promise<void>;
}

export interface Worksheet {
  id: string;
  worksheetName: string;
  worksheetReviewStatus?: ReviewStatus;
  templateMethodId: string;
  taskStatus: ProcessTaskStatusType;
  procedureRunConditionReplicate: ProcedureRunConditionReplicateNode;
  isInaccessible: boolean;
}

interface WorksheetNameWithStatusProps {
  name: string;
  taskStatus: ProcessTaskStatusType;
  reviewStatus?: ReviewStatus;
}

export function WorksheetNameWithStatusIcon({
  name,
  taskStatus,
  reviewStatus,
}: WorksheetNameWithStatusProps): JSX.Element {
  if (reviewStatus) {
    const badgeStatus = getBadgeStatus(reviewStatus);
    const style = STYLE_FOR_REVIEW_STATUS[badgeStatus];
    const icon = getStatusIcon(reviewStatus);
    return (
      <>
        <BenchlingIcon className={classnames(css(styles.reviewStatusIcon, style))} icon={icon} />
        {name}
      </>
    );
  } else {
    return (
      <>
        <TaskStatusIcon type={taskStatus} />
        {name}
      </>
    );
  }
}

export function SelectMethodAndWorksheetDropdowns({
  worksheetId,
  methodId,
  procedureRun,
  onSetWorksheet,
  onSetSelectedStep,
  selectedStep = null,
  refetch,
}: SelectWorksheetDropdownProps): JSX.Element {
  // Subscribe to updates from the root process of the procedure run to get updates for tasks in all PMEIs
  const rootProcessIds = procedureRun.methodExecutionInstances.map(({procedureRunConditionReplicate}) =>
    procedureRunConditionReplicate.processRootTask != null &&
    !isInaccessible(procedureRunConditionReplicate.processRootTask)
      ? procedureRunConditionReplicate.processRootTask.process.id
      : undefined
  );
  const uniqueProcessIds = new Set(rootProcessIds);
  assertAndContinue(uniqueProcessIds.size === 1, 'Expected exactly one root process for the procedure run', {
    uniqueProcessIds,
  });
  const rootProcessApiId = rootProcessIds[0];
  const allWorksheets: Array<Worksheet> = procedureRun.methodExecutionInstances.map(
    ({methodTask, templateMethodVersion, worksheet, procedureRunConditionReplicate}) => {
      const worksheetIsInaccessible = isInaccessibleInfo(worksheet);
      const worksheetId = getAccessibleOrInaccessibleId(worksheet);
      const worksheetName = worksheetIsInaccessible ? INACCESSIBLE_WORKSHEET_NAME : worksheet.name;
      const worksheetReviewStatus = worksheetIsInaccessible ? undefined : worksheet.review?.status;
      const taskStatus =
        methodTask && !isInaccessibleInfo(methodTask)
          ? methodTask.status.statusType
          : ProcessTaskStatusType.PENDING;

      return {
        id: worksheetId,
        worksheetName,
        worksheetReviewStatus,
        taskStatus,
        templateMethodId: getAccessibleOrInaccessibleId(templateMethodVersion),
        procedureRunConditionReplicate,
        isInaccessible: worksheetIsInaccessible,
      };
    }
  );
  const worksheetsForCurrentMethod = allWorksheets.filter(
    ({templateMethodId}) => methodId === templateMethodId
  );

  // build out dropdown options
  const methodOptions: Array<OptionData> = procedureRun.methodExecutionInstances.reduce(
    (methodOptions, methodExecutionInstance) => {
      const {name, sourceMethodId: id} = methodExecutionInstance.planMethodDefinitionVersion;
      if (!methodOptions.some((option) => option.value === id)) {
        methodOptions.push({name, value: id});
      }
      return methodOptions;
    },
    [] as Array<OptionData>
  );
  const worksheetDropdownOptions: Array<OptionData> = [];
  const worksheetIdsSeen = new Set<string>();
  worksheetsForCurrentMethod.forEach(
    ({id, worksheetName, worksheetReviewStatus, taskStatus, isInaccessible}) => {
      if (worksheetIdsSeen.has(id)) {
        return;
      }
      worksheetIdsSeen.add(id);
      worksheetDropdownOptions.push({
        name: (
          <WorksheetNameWithStatusIcon
            name={worksheetName}
            taskStatus={taskStatus}
            reviewStatus={worksheetReviewStatus}
          />
        ),
        pureTextName: worksheetName,
        value: id,
        disabled: isInaccessible,
      } satisfies OptionData);
    }
  );
  if (worksheetDropdownOptions.length > 1) {
    const taskStatusSummaryOption: OptionData = {
      name: <TaskStatusSummaryHeader worksheets={worksheetsForCurrentMethod} />,
      isHeader: true,
      className: css(styles.header),
    } satisfies OptionData;
    worksheetDropdownOptions.unshift(taskStatusSummaryOption);
  }

  function resetSelectedStep(): void {
    onSetSelectedStep({
      document: {status: DocumentStatus.NOT_SELECTED},
      stepGroupIndex: 0,
      stepIndex: 0,
    });
  }

  return (
    <div className={css(styles.worksheetNavigationComponent)}>
      <SelectDropdown
        displayName={methodOptions.find((option) => option.value === methodId)?.name || 'METHOD MISSING'}
        menu={
          <SearchableMenu
            selectedValue={methodId}
            height={Menu.HEIGHTS.SMALL}
            width={Menu.WIDTHS.FIT_CONTENT}
            scrollable={true}
            optionDatas={methodOptions}
            onSelect={(selectMethodId: OptionValue) => {
              // no-op if the selected method is the current method
              if (selectMethodId === methodId) {
                return;
              }

              const currentWorksheet = allWorksheets.filter(({id}) => id === worksheetId)[0];
              const correspondingMethodWorksheets = allWorksheets.filter(
                ({templateMethodId}) => templateMethodId === selectMethodId
              );

              const correspondingWorksheet =
                correspondingMethodWorksheets.find(
                  ({procedureRunConditionReplicate}) =>
                    procedureRunConditionReplicate.id === currentWorksheet.procedureRunConditionReplicate.id
                ) || correspondingMethodWorksheets[0]; // if it's undefined, set to the first worksheet

              // Todo(BNCH-91825): handle the case where this worksheet is inaccessible
              const switchedWorksheetTabs = onSetWorksheet(correspondingWorksheet.id);
              // skip step selection logic when we're just switching tabs
              if (!switchedWorksheetTabs) {
                // navigating to a unit ops in the same tab will always default select the first step
                resetSelectedStep();
              }
            }}
          />
        }
        alwaysDropDown={true}
        shouldEllipsize={true}
        className={css(styles.selectMethodDropdown)}
      />
      <BenchlingIcon icon="chevron-right" className={css(styles.chevron)} />
      <ProcessRealtimeUpdateSubscriptionWrapper
        apiId={rootProcessApiId}
        refetch={refetch}
        messageFilter="no_filter"
      >
        <SelectDropdown
          displayName={worksheetDropdownOptions.find((option) => option.value === worksheetId)?.name || ''}
          menu={
            <SearchableMenu
              selectedValue={worksheetId}
              height={Menu.HEIGHTS.SMALL}
              width={Menu.WIDTHS.FULL}
              className={css(styles.searchMenu)}
              scrollable={true}
              optionDatas={worksheetDropdownOptions}
              onSelect={(worksheetIdToSelect: OptionValue) => {
                const switchedWorksheetTabs = onSetWorksheet(worksheetIdToSelect);
                // skip step selection logic when we're just switching tabs
                if (!switchedWorksheetTabs) {
                  // TODO(BNCH-91147): implement corresponding step selection logic.
                  // selects first step by default
                  resetSelectedStep();
                }
              }}
            />
          }
          alwaysDropDown={true}
          shouldEllipsize={true}
          className={css(styles.selectWorksheetDropdown)}
        />
      </ProcessRealtimeUpdateSubscriptionWrapper>
    </div>
  );
}

export interface WorksheetNavigationComponentProps {
  worksheetId: string;
  worksheetName: string;
  onSetWorksheet: (id: string) => boolean;
  onSetSelectedStep: (stepSelection: WorksheetStepDocumentSelection) => void;
  selectedStep: WorksheetStepDocumentSelection | null;
}

export default function WorksheetNavigationComponent({
  worksheetId,
  worksheetName,
  onSetWorksheet,
  onSetSelectedStep,
  selectedStep,
}: WorksheetNavigationComponentProps): JSX.Element {
  const initialWorksheetId = useRef<string>(worksheetId);
  const [cachedProcedureRun, setCachedProcedureRun] = useState<ProcedureRunNode>();
  const {dispatch, worksheetViewState, isExecutionEnabled} = WorksheetContext.useContext();

  // Since we're only trying to get to the ProcedureRun from the Worksheet. The ProcedureRun will be the same
  // no matter what `worksheetId` you pass in for the run. So we'll always use initial one passed in.
  const procedureResult = useBenchlingQuery<PMEIProcedureQuery, PMEIProcedureQueryVariables>(
    PMEI_PROCEDURE_QUERY,
    {
      variables: {worksheetId: initialWorksheetId.current},
    }
  );
  const refetch = useCallback(async (): Promise<void> => {
    await procedureResult.refetch();
  }, [procedureResult]);

  useEffect(() => {
    if (
      procedureResult.type === QueryResultType.SUCCESS &&
      procedureResult.data.procedureMethodExecutionInstance &&
      !isInaccessibleInfo(procedureResult.data.procedureMethodExecutionInstance) &&
      cachedProcedureRun !== procedureResult.data.procedureMethodExecutionInstance.procedureRun &&
      worksheetViewState === WorksheetViewStateType.LOADED
    ) {
      // If the worksheet is loaded and the procedure is loaded and it is different from our previously cached
      // procedure run, we can set the new procedure run in the cache and mark the worksheet as PROCEDURE_LOADED.
      // This will, in turn, set isExecutionEnabled to true in the worksheet reducer to signal that the worksheet
      // is now fully ready.
      const procedureRun = procedureResult.data.procedureMethodExecutionInstance.procedureRun;
      dispatch({type: WorksheetViewActionType.PROCEDURE_LOADED, procedureType: procedureRun.procedureType});
      setCachedProcedureRun(procedureRun);
    } else if (
      procedureResult.type !== QueryResultType.PENDING &&
      worksheetViewState === WorksheetViewStateType.RELOADING &&
      cachedProcedureRun
    ) {
      // If we don't have a pending query in flight and the worksheet has been reloaded, we can reset the cached
      // procedure run and refetch the query.
      setCachedProcedureRun(undefined);
      refetch();
    }
  }, [procedureResult, cachedProcedureRun, dispatch, worksheetViewState, refetch, isExecutionEnabled]);

  const currentMethodExecutionInstance = cachedProcedureRun?.methodExecutionInstances.find(
    (method) => getAccessibleOrInaccessibleId(method.worksheet) === worksheetId
  );

  if (cachedProcedureRun != null && currentMethodExecutionInstance != null) {
    return (
      <div className={css(styles.container)}>
        <WorksheetStudyLink study={cachedProcedureRun.study} />
        <SelectMethodAndWorksheetDropdowns
          worksheetId={worksheetId}
          methodId={getAccessibleOrInaccessibleId(currentMethodExecutionInstance.templateMethodVersion)}
          procedureRun={cachedProcedureRun}
          onSetWorksheet={onSetWorksheet}
          onSetSelectedStep={onSetSelectedStep}
          selectedStep={selectedStep}
          refetch={refetch}
        />
      </div>
    );
  }
  return (
    <DisabledMethodAndWorksheetDropdowns worksheetName={worksheetName} methodName={LOADING_METHOD_NAME} />
  );
}

export const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  worksheetNavigationComponent: {
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
  },
  selectMethodDropdown: {
    width: '160px', // match the menu width
  },
  selectWorksheetDropdown: {
    width: '300px',
  },
  searchMenu: {width: '100%'},
  chevron: {
    padding: '10px',
    color: Color.GRAY_500,
  },
  taskStatusSummary: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
  },
  taskStatusNum: {
    paddingRight: '10px',
  },
  header: {
    borderBottom: `1px solid ${Color.GRAY_300}`,
    height: 'auto',
  },
  reviewStatusIcon: {
    marginRight: '6px',
  },
  accepted: {
    color: Color.GREEN_600,
  },
  needsReview: {
    color: Color.ORANGE_500,
  },
  inProgress: {
    color: Color.BLUE_500,
  },
  redButton: {
    color: Color.RED_500,
  },
});

export const STYLE_FOR_REVIEW_STATUS = {
  [ReviewBadgeStatus.ACCEPTED]: styles.accepted,
  [ReviewBadgeStatus.REJECTED]: styles.redButton,
  [ReviewBadgeStatus.IN_PROGRESS]: styles.inProgress,
  [ReviewBadgeStatus.IN_REVIEW]: styles.needsReview,
  [ReviewBadgeStatus.ACTION_REQUIRED]: styles.redButton,
};
